import { z } from "zod";
import { AgentId, AgentVersionId, GameId, PresetId, Timestamp, UserId } from "../../common";
import { PLAYER_BUCKET_ORDER } from "../shared/buckets.js";
import { PRINTABLE_ASCII } from "../shared/source-id";

const examples = {
  userId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
  coPayerId: "0199f0be-9c07-7a24-b1e8-3f5d8a1c6207",
  holdId: "184467",
  holdRequestId: "01926f3a-7b4c-7d2e-9f10-3a5b6c7d8e9f",
  presetId: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
  gameId: "9d2f6c1e-3b7a-4f0e-8c21-5a6d7e8f9a0b",
  asOf: "2026-09-16T14:02:11.402Z",
} as const;

/** The inclusive bounds of a signed PostgreSQL `bigint`, which every raw amount has to fit. */
const RAW_MAX = 9223372036854775807n;
const RAW_MIN = -9223372036854775808n;

/** Base-10 integer text: an optional minus sign, then `0` or a digit string with no leading zero. */
const SIGNED_INTEGER = /^(0|-?[1-9]\d*)$/;
const UNSIGNED_INTEGER = /^(0|[1-9]\d*)$/;
const POSITIVE_INTEGER = /^[1-9]\d*$/;

/** A SHA-256 value written as 64 lowercase hexadecimal characters. */

/** Runs after the shape check, so text that is not an integer at all is already refused there. */
function fitsBigint(value: string): boolean {
  try {
    const parsed = BigInt(value);
    return parsed >= RAW_MIN && parsed <= RAW_MAX;
  } catch {
    return true;
  }
}

const withinBigint = {
  error: "The amount is outside the range of a signed 64-bit integer.",
} as const;

/** Caller-supplied text that becomes all or part of an operation's source id. */
function sourceIdPart(maxLength: number) {
  return z
    .string()
    .min(1)
    .max(maxLength)
    .regex(PRINTABLE_ASCII, { error: "Use printable ASCII characters only." });
}

export const HoldId = z
  .string()
  .regex(POSITIVE_INTEGER)
  .refine(fitsBigint, withinBigint)
  .brand<"HoldId">()
  .meta({
    id: "HoldId",
    description: "The identity the ledger gives one hold. Settling or releasing it names this id.",
    example: examples.holdId,
  });
export type HoldId = z.infer<typeof HoldId>;

export const HoldRequestId = z.uuid({ version: "v7" }).brand<"HoldRequestId">().meta({
  id: "HoldRequestId",
  description:
    "The holder's UUIDv7 for one hold, which makes placing it safe to retry. The model gateway uses the model call's `call_id`.",
  example: examples.holdRequestId,
});
export type HoldRequestId = z.infer<typeof HoldRequestId>;

export const OperationId = z
  .string()
  .regex(POSITIVE_INTEGER)
  .refine(fitsBigint, withinBigint)
  .brand<"OperationId">()
  .meta({
    id: "OperationId",
    description: "The database identity of the operation that recorded this effect.",
    example: "42",
  });
export type OperationId = z.infer<typeof OperationId>;

export const RawPaxAmount = z
  .string()
  .regex(SIGNED_INTEGER)
  .refine(fitsBigint, withinBigint)
  .brand<"RawPaxAmount">()
  .meta({
    id: "RawPaxAmount",
    description:
      "A signed count of raw Pax tokens, written as a base-10 integer string. A balance is the one figure that may be negative.",
    example: "64500000000",
  });
export type RawPaxAmount = z.infer<typeof RawPaxAmount>;

export const RawPaxTotal = z
  .string()
  .regex(UNSIGNED_INTEGER)
  .refine(fitsBigint, withinBigint)
  .brand<"RawPaxTotal">()
  .meta({
    id: "RawPaxTotal",
    description:
      "A count of raw Pax tokens that is zero or greater, written as a base-10 integer string.",
    example: "5000000000",
  });
export type RawPaxTotal = z.infer<typeof RawPaxTotal>;

export const RawPaxQuantity = z
  .string()
  .regex(POSITIVE_INTEGER)
  .refine(fitsBigint, withinBigint)
  .brand<"RawPaxQuantity">()
  .meta({
    id: "RawPaxQuantity",
    description:
      "How many raw Pax tokens to move, written as a base-10 integer string greater than zero. The endpoint decides the direction.",
    example: "3000000000000",
  });
export type RawPaxQuantity = z.infer<typeof RawPaxQuantity>;

export const RawPaxSum = z.string().regex(UNSIGNED_INTEGER).brand<"RawPaxSum">().meta({
  id: "RawPaxSum",
  description:
    "A sum of raw Pax tokens that is zero or greater, written as a base-10 integer string of any length.",
  example: "120000000000",
});
export type RawPaxSum = z.infer<typeof RawPaxSum>;

export const SignedRawPaxSum = z.string().regex(SIGNED_INTEGER).brand<"SignedRawPaxSum">().meta({
  id: "SignedRawPaxSum",
  description:
    "A sum of raw Pax tokens that may be negative, written as a base-10 integer string of any length.",
  example: "-5000000000",
});
export type SignedRawPaxSum = z.infer<typeof SignedRawPaxSum>;

export const LedgerPerson = z
  .object({
    userId: UserId,
    username: z.string().nullable().meta({
      description: "Their handle, or null while they have not claimed one.",
    }),
    displayName: z.string().min(1),
  })
  .meta({
    id: "LedgerPerson",
    description: "A person named in a ledger read, as their profile reads right now.",
    example: { userId: examples.userId, username: "ada", displayName: "Ada" },
  });
export type LedgerPerson = z.infer<typeof LedgerPerson>;

export const PlayerBucket = z
  .enum([
    "daily",
    "moderation",
    "signup",
    "sponsorship",
    "commission",
    "compensation",
    "subscription",
    "oneTimePurchase",
  ])
  .meta({
    id: "PlayerBucket",
    description: "One of the eight balances a player's credits are held in.",
    example: "daily",
  });
export type PlayerBucket = z.infer<typeof PlayerBucket>;

const _sharedPlayerBucketsMatchSchema: readonly PlayerBucket[] = PLAYER_BUCKET_ORDER;
const _schemaPlayerBucketsMatchShared: typeof PLAYER_BUCKET_ORDER = [
  "daily",
  "moderation",
  "signup",
  "sponsorship",
  "commission",
  "compensation",
  "subscription",
  "oneTimePurchase",
] as const satisfies readonly PlayerBucket[];

export const BucketBalances = z
  .object({
    daily: RawPaxAmount.meta({
      description: "Credits from daily check-ins.",
      example: "10000000000",
    }),
    moderation: RawPaxAmount.meta({
      description: "Credits that fund the moderation hold kinds.",
      example: "2500000000",
    }),
    signup: RawPaxAmount.meta({
      description: "Credits from the signup grant.",
      example: "0",
    }),
    sponsorship: RawPaxAmount.meta({
      description: "Credits received as sponsorship gifts.",
      example: "0",
    }),
    commission: RawPaxAmount.meta({
      description: "Creator earnings from other players using their presets.",
      example: "0",
    }),
    compensation: RawPaxAmount.meta({
      description: "Pay for community management and flag moderation.",
      example: "0",
    }),
    subscription: RawPaxAmount.meta({
      description: "Credits from paid token-subscription invoices.",
      example: "0",
    }),
    oneTimePurchase: RawPaxAmount.meta({
      description: "Purchased credits. This is the one balance that can be negative.",
      example: "64500000000",
    }),
  })
  .meta({
    id: "BucketBalances",
    description: "The eight balances a player's credits sit in, each keyed by its own name.",
  });
export type BucketBalances = z.infer<typeof BucketBalances>;

export const BucketAmounts = BucketBalances.partial().meta({
  id: "BucketAmounts",
  description: "An amount per named balance. Balances left out are untouched.",
  example: { oneTimePurchase: "620000000000", daily: "4000000000" },
});
export type BucketAmounts = z.infer<typeof BucketAmounts>;

export const RefreshEligibility = z
  .object({
    claimable: z.boolean().meta({ description: "Whether a claim succeeds right now." }),
    nextClaimableAt: Timestamp.nullable().meta({
      description: "When the next claim becomes eligible. `null` while a claim is available.",
    }),
  })
  .meta({
    id: "RefreshEligibility",
    description: "Whether a refresh can be claimed, and when it can be if not.",
    example: { claimable: true, nextClaimableAt: null },
  });
export type RefreshEligibility = z.infer<typeof RefreshEligibility>;

export const UserParams = z
  .strictObject({
    userId: UserId,
  })
  .meta({
    id: "UserParams",
    description: "A route parameter naming one player by their user id.",
    example: { userId: examples.userId },
  });
export type UserParams = z.infer<typeof UserParams>;

const HeldRaw = RawPaxTotal.meta({
  description:
    "What this player's open holds still reserve, ordinary and moderation together. A hold past its deadline is no longer counted here.",
  example: "500000000",
});

const exampleBuckets = {
  daily: "10000000000",
  moderation: "2500000000",
  signup: "0",
  sponsorship: "0",
  commission: "0",
  compensation: "0",
  subscription: "0",
  oneTimePurchase: "64500000000",
} as const;

export const ClientAllowance = z
  .object({
    unlimited: z.boolean().meta({
      description:
        "True while the player holds purchased credits, which lift the allowance entirely.",
      example: false,
    }),
    allowanceRaw: RawPaxTotal.meta({
      description: "What direct AI calls may spend from free credits in any 24 hours.",
      example: "7500000000",
    }),
    remainingRaw: RawPaxTotal.meta({
      description: "What is left of it now, counting calls still running at what they hold.",
      example: "5200000000",
    }),
    resetsAt: Timestamp.nullable().meta({
      description:
        "When enough of the last day's spending rolls off for direct AI calls to be admitted again, or null when they are admitted now.",
      example: null,
    }),
  })
  .meta({
    id: "ClientAllowance",
    description:
      "The daily share of free credits a player's own direct AI calls may spend. Gameplay calls don't count against it.",
  });
export type ClientAllowance = z.infer<typeof ClientAllowance>;

export const BalanceResponse = z
  .object({
    asOf: Timestamp.meta({
      description: "When these figures were true.",
      example: examples.asOf,
    }),
    buckets: BucketBalances,
    heldRaw: HeldRaw,
    refresh: RefreshEligibility,
    clientAllowance: ClientAllowance,
  })
  .meta({
    id: "BalanceResponse",
    description:
      "A player's credits, what their open holds reserve, their refresh state, and what is left of their direct AI call allowance.",
    example: {
      asOf: examples.asOf,
      buckets: exampleBuckets,
      heldRaw: "500000000",
      refresh: { claimable: true, nextClaimableAt: null },
      clientAllowance: {
        unlimited: false,
        allowanceRaw: "7500000000",
        remainingRaw: "5200000000",
        resetsAt: null,
      },
    },
  });
export type BalanceResponse = z.infer<typeof BalanceResponse>;

export const BalanceRequestHeaders = z
  .object({
    "if-none-match": z.string().optional().meta({
      description:
        "The `etag` from an earlier read. The route answers 304 with no body while the credits still match it.",
      example: '"9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08"',
    }),
  })
  .meta({
    id: "BalanceRequestHeaders",
    description: "The conditional-read header both balance routes accept.",
  });
export type BalanceRequestHeaders = z.infer<typeof BalanceRequestHeaders>;

export const HoldKind = z.string().min(1).max(64).meta({
  id: "HoldKind",
  description:
    "What the hold is for, which decides its creator reward and whether it counts toward the client allowance.",
  example: "game_master_model_call",
});
export type HoldKind = z.infer<typeof HoldKind>;

export const HoldStatus = z.enum(["open", "settled", "released"]).meta({
  id: "HoldStatus",
  description:
    "`open`: the hold still reserves its estimate. `settled`: charged its measured amount. `released`: ended with nobody charged.",
  example: "open",
});
export type HoldStatus = z.infer<typeof HoldStatus>;

/** Each code followed by what it means, as one line of a description. */
function codeMeanings(meanings: Record<string, string>) {
  return Object.entries(meanings)
    .map(([code, meaning]) => `\`${code}\`: ${meaning}`)
    .join(" ");
}

const RELEASE_REASONS = ["released", "not_sent", "abandoned"] as const;

/** What each release reason means, in plain English. */
export const RELEASE_REASON_MEANINGS: Record<(typeof RELEASE_REASONS)[number], string> = {
  released: "The holder said nothing will be charged.",
  not_sent: "The holder never started the work.",
  abandoned: "The hold passed its deadline before it settled.",
};

export const ReleaseReason = z.enum(RELEASE_REASONS).meta({
  id: "ReleaseReason",
  description: `Why a hold ended with nobody charged. ${codeMeanings(RELEASE_REASON_MEANINGS)}`,
  example: "released",
});
export type ReleaseReason = z.infer<typeof ReleaseReason>;

/** The game and preset a hold was placed for, with titles where the route looks them up. */
const holdSubject = {
  kind: HoldKind,
  presetId: PresetId.nullable().meta({
    description: "The game's preset. Null for a signed-in user's own hold, placed outside a game.",
  }),
  presetTitle: z.string().nullable().optional().meta({
    description: "The preset's title, from its active version or else its newest one.",
  }),
  gameId: GameId.nullable().meta({
    description: "The game the hold was placed for. Null for a signed-in user's own hold.",
  }),
  gameTitle: z.string().nullable().optional().meta({
    description: "The game's title. Null once the game is deleted.",
  }),
};

export const CallAgent = z
  .object({
    id: AgentId,
    versionId: AgentVersionId,
    name: z.string(),
    version: z.number().int().positive(),
  })
  .meta({
    id: "CallAgent",
    description: "The published agent version that made a model call, as ClickHouse recorded it.",
    example: {
      id: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
      versionId: "f176c8e6-c8ed-5d9b-85b4-3c176df9595f",
      name: "GPT-6 Luna",
      version: 3,
    },
  });
export type CallAgent = z.infer<typeof CallAgent>;

const holdAgent = CallAgent.nullable().meta({
  description:
    "The agent version whose model call the hold reserved for. Null when the call had none, or ClickHouse has no record of it.",
});

export const HeldFor = z
  .object({
    ...holdSubject,
    estimateRaw: RawPaxTotal.nullable().meta({
      description:
        "What the hold reserved from the player whose history this is, or null outside a player's history.",
      example: "500000000",
    }),
    agent: holdAgent,
  })
  .meta({ id: "HeldFor", description: "The kind, game, and preset of the hold behind a change." });
export type HeldFor = z.infer<typeof HeldFor>;

export const OpenHold = z
  .object({
    holdId: HoldId,
    holdRequestId: HoldRequestId,
    status: HoldStatus.meta({ example: "open" }),
    estimateRaw: RawPaxTotal.meta({
      description: "What this player's share of the hold reserved when it was placed.",
      example: "500000000",
    }),
    startedAt: Timestamp.meta({ description: "When the hold was placed.", example: examples.asOf }),
    expiresAt: Timestamp.meta({
      description:
        "The hold's deadline. Past it the hold is abandoned: it reserves nothing, counts toward no limit, and leaves this list.",
      example: "2026-09-16T14:10:11.402Z",
    }),
    ...holdSubject,
  })
  .meta({
    id: "OpenHold",
    description: "One ordinary hold before its deadline, not yet settled or released.",
  });
export type OpenHold = z.infer<typeof OpenHold>;

export const OpenHoldsResponse = z
  .object({
    openHolds: z.array(OpenHold).meta({
      description: "The player's ordinary holds before their deadline, newest first.",
    }),
    reservedTotalRaw: RawPaxTotal.meta({
      description: "What the open holds reserve, summed.",
      example: "500000000",
    }),
  })
  .meta({
    id: "OpenHoldsResponse",
    description: "A player's open ordinary holds and what they still reserve.",
    example: {
      openHolds: [
        {
          holdId: examples.holdId,
          holdRequestId: examples.holdRequestId,
          status: "open",
          estimateRaw: "500000000",
          startedAt: examples.asOf,
          expiresAt: "2026-09-16T14:10:11.402Z",
          kind: "game_master_model_call",
          presetId: examples.presetId,
          gameId: examples.gameId,
        },
      ],
      reservedTotalRaw: "500000000",
    },
  });
export type OpenHoldsResponse = z.infer<typeof OpenHoldsResponse>;

export const CreditJournal = z
  .enum([
    "aiSettlement",
    "chargebackRemoval",
    "closureRemoval",
    "compensationGrant",
    "creatorRewardDeposit",
    "dailyExpiration",
    "dailyGrant",
    "debtRepayment",
    "failedPaymentRemoval",
    "moderationExpiration",
    "moderationGrant",
    "openingBalance",
    "otherReward",
    "pendingCreatorReward",
    "purchasedCredits",
    "refundRemoval",
    "signupGrant",
    "sponsorshipGrant",
    "subscriptionGrant",
    "supportGrant",
    "supportRemoval",
  ])
  .meta({
    id: "CreditJournal",
    description: "Why a player's credits moved.",
    example: "aiSettlement",
  });
export type CreditJournal = z.infer<typeof CreditJournal>;

/** The most changes one history page holds. */
const CREDIT_HISTORY_PAGE_MAX = 50;

export const CreditHistoryQuery = z
  .strictObject({
    before: z.string().regex(POSITIVE_INTEGER).refine(fitsBigint, withinBigint).optional().meta({
      description:
        "Read changes older than this entry: the `nextBefore` of the previous page. Leave it out for the newest page.",
      example: "9012",
    }),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(CREDIT_HISTORY_PAGE_MAX)
      .default(CREDIT_HISTORY_PAGE_MAX)
      .meta({
        description: "How many changes to return.",
        example: CREDIT_HISTORY_PAGE_MAX,
      }),
  })
  .meta({
    id: "CreditHistoryQuery",
    description: "Where the page starts and how many changes it holds.",
    example: { before: "9012", limit: CREDIT_HISTORY_PAGE_MAX },
  });
export type CreditHistoryQuery = z.infer<typeof CreditHistoryQuery>;

export const ChargedModelCall = z
  .object({
    callId: z.uuid().meta({
      description: "The model call's id; same as the hold's `holdRequestId`.",
    }),
    payerCount: z.number().int().positive().meta({
      description:
        "How many players the charge was split between, this player included. `1` means they paid it all.",
      example: 1,
    }),
    agent: CallAgent.nullable().meta({
      description: "The published agent version that made the call, when it had one.",
    }),
    game: z
      .object({ id: GameId, title: z.string() })
      .nullable()
      .meta({ description: "The game the call was made for, when it was made for one." }),
  })
  .meta({
    id: "ChargedModelCall",
    description:
      "The model call an AI usage charge paid for. Agent and game arrive once the call is recorded, a moment after it settles.",
    example: {
      callId: "5b7e2c4a-8d1f-4e3b-a6c9-0d2e4f6a8b1c",
      payerCount: 1,
      agent: {
        id: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
        versionId: "f176c8e6-c8ed-5d9b-85b4-3c176df9595f",
        name: "GPT-6 Luna",
        version: 3,
      },
      game: { id: examples.gameId, title: "The Long Winter" },
    },
  });
export type ChargedModelCall = z.infer<typeof ChargedModelCall>;

export const CreditChange = z
  .object({
    entryId: z.string().regex(POSITIVE_INTEGER).meta({
      description: "The ledger entry this change was recorded under.",
      example: "9031",
    }),
    recordedAt: Timestamp.meta({
      description: "When the change was recorded.",
      example: examples.asOf,
    }),
    journal: CreditJournal,
    presetId: PresetId.optional().meta({
      description: "The preset that earned a creator-reward deposit.",
      example: examples.presetId,
    }),
    buckets: BucketAmounts.meta({
      description:
        "What each balance gained, as a positive amount, or lost, as a negative one. Balances left out did not move.",
      example: { daily: "-4000000000", signup: "-2000000000" },
    }),
    modelCall: ChargedModelCall.nullable().meta({
      description: "For an `aiSettlement` change, the call it paid for. Null otherwise.",
    }),
  })
  .meta({
    id: "CreditChange",
    description: "One movement of the player's credits.",
  });
export type CreditChange = z.infer<typeof CreditChange>;

export const CreditHistoryResponse = z
  .object({
    changes: z.array(CreditChange).meta({
      description: "Up to `limit` changes to the player's balances, newest first.",
    }),
    nextBefore: z.string().regex(POSITIVE_INTEGER).nullable().meta({
      description:
        "The `before` value for the next older page: the entry id of the oldest change here. Null when this page reaches the start of the player's history.",
      example: "9012",
    }),
  })
  .meta({
    id: "CreditHistoryResponse",
    description: "One page of what granted and spent the current player's credits.",
    example: {
      changes: [
        {
          entryId: "9031",
          recordedAt: examples.asOf,
          journal: "aiSettlement",
          buckets: { daily: "-4000000000", signup: "-2000000000" },
          modelCall: {
            callId: "5b7e2c4a-8d1f-4e3b-a6c9-0d2e4f6a8b1c",
            payerCount: 3,
            agent: {
              id: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
              versionId: "f176c8e6-c8ed-5d9b-85b4-3c176df9595f",
              name: "GPT-6 Luna",
              version: 3,
            },
            game: { id: examples.gameId, title: "The Long Winter" },
          },
        },
        {
          entryId: "9012",
          recordedAt: "2026-09-16T09:14:02.000Z",
          journal: "dailyGrant",
          buckets: { daily: "10000000000" },
          modelCall: null,
        },
      ],
      nextBefore: "9012",
    },
  });
export type CreditHistoryResponse = z.infer<typeof CreditHistoryResponse>;

export const EarningsDay = z.iso.date().meta({
  id: "EarningsDay",
  description: "A UTC calendar day, written as YYYY-MM-DD.",
  example: "2026-09-25",
});
export type EarningsDay = z.infer<typeof EarningsDay>;

export const EarningsRange = z.enum(["30d", "90d", "365d"]).meta({
  id: "EarningsRange",
  description:
    "How many whole UTC days to read, counting back from today's UTC day and including it.",
  example: "30d",
});
export type EarningsRange = z.infer<typeof EarningsRange>;

/** How many UTC days each earnings range covers. */
export const EARNINGS_RANGE_DAYS: Record<EarningsRange, number> = {
  "30d": 30,
  "90d": 90,
  "365d": 365,
};

const earningsRange = EarningsRange.default("30d");

/** The furthest back, in whole ranges, a chart can step. */
export const MAX_PERIODS_BACK = 1000;

/** How many ranges back to read, so a chart can step to the period before. */
const periodsBack = (description: string) =>
  z.coerce.number().int().min(0).max(MAX_PERIODS_BACK).default(0).meta({ description, example: 1 });

export const EarningsQuery = z
  .strictObject({
    userId: UserId.optional().meta({
      description:
        "The recipient. Leave it out with `presetId` for every recipient of that preset, or for only the caller's share when they may not read the others.",
    }),
    presetId: PresetId.optional().meta({ description: "Limit the result to this preset." }),
    range: earningsRange,
    back: periodsBack(
      "How many ranges back to read: 0 for the newest, which includes today; 1 for the one just before it; and so on.",
    ),
  })
  .meta({
    id: "EarningsQuery",
    description:
      "Whose creator earnings to read, and over which named range. With no recipient and no preset, the caller's own.",
    example: { presetId: examples.presetId, range: "30d" },
  });
export type EarningsQuery = z.infer<typeof EarningsQuery>;

const earningsWindow = {
  from: EarningsDay.meta({ description: "The first UTC day read." }),
  to: EarningsDay.meta({
    description: "The UTC day the read ends before: tomorrow, for the newest range.",
  }),
};

const earnedRaw = RawPaxSum.meta({
  description: "The creator rewards paid, in raw credits.",
  example: "1200000000",
});
const rewardCount = z.number().int().nonnegative().optional().meta({
  description: "How many rewards were paid. Only callers with `credits.view` receive it.",
  example: 4,
});

export const EarningsDayTotal = z
  .object({
    earnedOn: EarningsDay.meta({ description: "The UTC day the rewards were paid." }),
    earnedRaw,
    rewardCount,
  })
  .meta({
    id: "EarningsDayTotal",
    description: "The creator earnings matching the query on one UTC day.",
  });
export type EarningsDayTotal = z.infer<typeof EarningsDayTotal>;

const earningsDays = z.array(EarningsDayTotal).meta({
  description: "One row per UTC day with a reward, oldest first. Other days are absent.",
});

export const EarningsStatus = z
  .object({
    hasEarnings: z.boolean().meta({
      description: "Whether the caller has ever been paid a creator reward.",
      example: true,
    }),
  })
  .meta({ id: "EarningsStatus", description: "Whether the caller has any creator earnings." });
export type EarningsStatus = z.infer<typeof EarningsStatus>;

export const EarningsByDayPart = z
  .object({
    presetId: PresetId.nullable().optional().meta({
      description:
        "The preset this part was earned from, when split by preset. Null for every preset the recipient does not own, added together.",
    }),
    userId: UserId.optional().meta({
      description: "The recipient this part was paid to, when split by recipient.",
    }),
    label: z.string().meta({
      description: "The preset's title or the recipient's name.",
      example: "Clockwork",
    }),
    days: earningsDays,
  })
  .meta({
    id: "EarningsByDayPart",
    description: "One preset's or one recipient's share of the earnings, per UTC day.",
  });
export type EarningsByDayPart = z.infer<typeof EarningsByDayPart>;

export const EarningsByDayResponse = z
  .object({
    ...earningsWindow,
    paidThrough: EarningsDay.nullable().meta({
      description:
        "The UTC day creator rewards are paid up to, exclusive: the day after the latest day the nightly payout finished. Days from it on can still gain earnings. Null until a payout finishes.",
    }),
    days: earningsDays,
    parts: z.array(EarningsByDayPart).meta({
      description:
        "With only a recipient, their earnings from each preset they own, then from every other preset. With only a preset, what it paid each recipient. Largest first. Empty with both.",
    }),
  })
  .meta({
    id: "EarningsByDayResponse",
    description: "The creator earnings matching the query, per UTC day, in total and in parts.",
    example: {
      from: "2026-09-02",
      to: "2026-10-02",
      paidThrough: "2026-10-01",
      days: [{ earnedOn: "2026-09-25", earnedRaw: "600000000", rewardCount: 2 }],
      parts: [
        {
          presetId: examples.presetId,
          label: "Clockwork",
          days: [{ earnedOn: "2026-09-25", earnedRaw: "600000000", rewardCount: 2 }],
        },
      ],
    },
  });
export type EarningsByDayResponse = z.infer<typeof EarningsByDayResponse>;

export const ClaimCreditRefreshResponse = z
  .object({
    operationId: OperationId,
    dailyGrantedRaw: RawPaxTotal.meta({
      description: "The fresh daily credits this claim added.",
      example: "10000000000",
    }),
    dailyExpiredRaw: RawPaxTotal.meta({
      description: "The previous unused daily remainder expired by this claim.",
      example: "2000000000",
    }),
    moderationGrantedRaw: RawPaxTotal.meta({
      description: "The fresh moderation credits this claim added.",
      example: "4000000000",
    }),
    moderationExpiredRaw: RawPaxTotal.meta({
      description: "The previous unused moderation remainder expired by this claim.",
      example: "1000000000",
    }),
    debtRepaidRaw: RawPaxTotal.meta({
      description: "One-time purchase debt repaid out of the new credits.",
      example: "3500000000",
    }),
    refreshedAt: Timestamp.meta({
      description: "When the refresh was recorded.",
      example: "2026-09-16T09:14:02.000Z",
    }),
  })
  .meta({
    id: "ClaimCreditRefreshResponse",
    description: "Everything the credit refresh changed.",
  });
export type ClaimCreditRefreshResponse = z.infer<typeof ClaimCreditRefreshResponse>;

export const ClaimCreditsResponse = z
  .object({
    signupGrantedRaw: RawPaxTotal.meta({
      description:
        "What this claim added to the signup balance: the signup amount on a first claim, the difference after a guest upgrades, and otherwise zero.",
      example: "1000000000",
    }),
    refresh: ClaimCreditRefreshResponse.nullable().meta({
      description:
        "The credit refresh this claim recorded, or null for a guest or when none is due.",
    }),
  })
  .meta({
    id: "ClaimCreditsResponse",
    description: "The free credits one claim granted.",
  });
export type ClaimCreditsResponse = z.infer<typeof ClaimCreditsResponse>;

export const SignupBudgetSpentData = z
  .strictObject({
    retryAt: Timestamp.meta({
      description: "When the spent hourly or daily signup budget resets.",
      example: "2026-09-16T10:00:00.000Z",
    }),
  })
  .meta({
    id: "SignupBudgetSpentData",
    description: "When signup claims are accepted again.",
  });
export type SignupBudgetSpentData = z.infer<typeof SignupBudgetSpentData>;

export const RecordedCreditChange = z
  .object({
    operationId: OperationId,
    recordedAt: Timestamp.meta({
      description: "When the effect was recorded.",
      example: "2026-09-16T16:04:48.106Z",
    }),
    bucket: PlayerBucket.meta({
      description: "The balance this command changed.",
      example: "oneTimePurchase",
    }),
    amountRaw: RawPaxQuantity.meta({
      description: "How much moved.",
      example: "3000000000000",
    }),
  })
  .meta({
    id: "RecordedCreditChange",
    description: "The credit effect this command recorded.",
  });
export type RecordedCreditChange = z.infer<typeof RecordedCreditChange>;

export const CreateSupportAdjustmentResponse = RecordedCreditChange.meta({
  id: "CreateSupportAdjustmentResponse",
  description: "The grant or removal the support adjustment recorded.",
  example: {
    operationId: "42",
    recordedAt: "2026-09-16T16:04:48.106Z",
    bucket: "daily",
    amountRaw: "50000000000",
  },
});
export type CreateSupportAdjustmentResponse = z.infer<typeof CreateSupportAdjustmentResponse>;

/** The longest note a support adjustment may carry. */
export const SUPPORT_NOTE_MAX_CHARACTERS = 500;

export const SupportAdjustmentReason = z
  .enum(["competition_winner", "refund", "creator_sponsorship", "deduction", "other"])
  .meta({
    id: "SupportAdjustmentReason",
    description:
      "The reason a staff member picked from the support tool's list. Any reason goes with either direction.",
    example: "refund",
  });
export type SupportAdjustmentReason = z.infer<typeof SupportAdjustmentReason>;

export const SupportAdjustmentDirection = z.enum(["grant", "removal"]).meta({
  id: "SupportAdjustmentDirection",
  description: "`grant` adds credits to the balance. `removal` takes them out.",
  example: "grant",
});
export type SupportAdjustmentDirection = z.infer<typeof SupportAdjustmentDirection>;

export const SupportAdjustmentRequest = z
  .strictObject({
    direction: SupportAdjustmentDirection,
    userId: UserId,
    bucket: PlayerBucket.meta({
      description: "The balance this adjustment lands on.",
      example: "daily",
    }),
    amountRaw: RawPaxQuantity.meta({ example: "50000000000" }),
    adjustmentRequestId: sourceIdPart(128).meta({
      description: "The caller's id for this adjustment, chosen once and reused on every retry.",
      example: "adj_8823",
    }),
    reason: SupportAdjustmentReason,
    note: z.string().trim().min(1).max(SUPPORT_NOTE_MAX_CHARACTERS).meta({
      description: "Why the staff member made this adjustment, kept with it for later readers.",
      example: "Refund for a game that failed to start.",
    }),
  })
  .meta({
    id: "SupportAdjustmentRequest",
    description:
      "Grant or removal, who, which balance, how much, why, and the caller's id for the adjustment.",
    example: {
      direction: "grant",
      userId: examples.userId,
      bucket: "daily",
      amountRaw: "50000000000",
      adjustmentRequestId: "adj_8823",
      reason: "refund",
      note: "Refund for a game that failed to start.",
    },
  });
export type SupportAdjustmentRequest = z.infer<typeof SupportAdjustmentRequest>;

export const ClosePlayerRequest = z
  .strictObject({
    closureRequestId: sourceIdPart(128).meta({
      description: "The caller's id for this closure, chosen once and reused on every retry.",
      example: "closure_8823",
    }),
    removeBalances: z
      .array(PlayerBucket)
      .meta({
        description: "The balances to zero when the player closes. Each name may appear once.",
        example: ["signup", "daily"],
      })
      .superRefine((balances, ctx) => {
        const seen = new Set<string>();
        for (const [index, bucket] of balances.entries()) {
          if (seen.has(bucket)) {
            ctx.addIssue({
              code: "custom",
              message: "Each balance may appear once.",
              path: [index],
            });
          }
          seen.add(bucket);
        }
      }),
  })
  .meta({
    id: "ClosePlayerRequest",
    description: "Which balances to clear and the caller's id for the closure.",
    example: {
      closureRequestId: "closure_8823",
      removeBalances: ["signup", "daily"],
    },
  });
export type ClosePlayerRequest = z.infer<typeof ClosePlayerRequest>;

export const ClosePlayerResponse = z
  .object({
    operationId: OperationId,
    closedAt: Timestamp.meta({
      description: "When the player was closed.",
      example: "2026-09-16T16:04:48.106Z",
    }),
    removed: BucketAmounts.meta({
      description: "The amount removed from each balance the request named.",
    }),
  })
  .meta({
    id: "ClosePlayerResponse",
    description: "The closure that was recorded.",
    example: {
      operationId: "42",
      closedAt: "2026-09-16T16:04:48.106Z",
      removed: { signup: "50000000000", daily: "0" },
    },
  });
export type ClosePlayerResponse = z.infer<typeof ClosePlayerResponse>;

/** The most display credits all staff together may grant as support in one UTC day. */
export const MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY = 100;

export const SupportAdjustment = z
  .object({
    operationId: OperationId,
    recordedAt: Timestamp.meta({ description: "When the adjustment was recorded." }),
    granter: LedgerPerson.meta({ description: "The staff member who made it." }),
    recipient: LedgerPerson.meta({ description: "The player whose balance it changed." }),
    bucket: PlayerBucket.meta({ description: "The balance it changed." }),
    amountRaw: RawPaxAmount.meta({
      description: "Positive for a grant, negative for a removal.",
      example: "-50000000000",
    }),
    reason: SupportAdjustmentReason,
    note: z.string().meta({ description: "Why the staff member made it." }),
  })
  .meta({ id: "SupportAdjustment", description: "One support grant or removal, and who made it." });
export type SupportAdjustment = z.infer<typeof SupportAdjustment>;

const OperationCursor = z
  .string()
  .regex(POSITIVE_INTEGER)
  .refine(fitsBigint, withinBigint)
  .meta({ example: "9012" });

export const ListSupportAdjustmentsQuery = z
  .strictObject({
    granterUserId: UserId.optional().meta({
      description: "Only adjustments this staff member made.",
    }),
    recipientUserId: UserId.optional().meta({ description: "Only adjustments to this player." }),
    from: Timestamp.optional().meta({ description: "Only adjustments recorded at or after this." }),
    to: Timestamp.optional().meta({ description: "Only adjustments recorded before this." }),
    beforeOperationId: OperationCursor.optional().meta({
      description:
        "Read adjustments older than this operation: the `nextBeforeOperationId` of the previous page.",
    }),
  })
  .meta({
    id: "ListSupportAdjustmentsQuery",
    description: "Which support adjustments to list, and where the page starts.",
  });
export type ListSupportAdjustmentsQuery = z.infer<typeof ListSupportAdjustmentsQuery>;

export const ListSupportAdjustmentsResponse = z
  .object({
    adjustments: z.array(SupportAdjustment).meta({ description: "Up to 50, newest first." }),
    nextBeforeOperationId: OperationId.nullable().meta({
      description: "The cursor for the next older page, or null on the last page.",
    }),
  })
  .meta({ id: "ListSupportAdjustmentsResponse", description: "One page of support adjustments." });
export type ListSupportAdjustmentsResponse = z.infer<typeof ListSupportAdjustmentsResponse>;

const PLAYER_HISTORY_CUT = /^([1-9]\d*)?\.([1-9]\d*)?$/;

export const PlayerHistoryCut = z
  .string()
  .regex(PLAYER_HISTORY_CUT)
  .refine(cut => cut !== "." && cut.split(".").every(id => id === "" || fitsBigint(id)), {
    error: "Name an operation id, a hold id, or both, each within a signed 64-bit integer.",
  })
  .brand<"PlayerHistoryCut">()
  .meta({
    id: "PlayerHistoryCut",
    description:
      "Where two pages of a player's history meet, as `<operationId>.<holdId>`. The older page holds the operations before that operation and the released holds before that hold. An empty side bounds nothing: that kind reads from `at`, or from now.",
    example: "9012.184467",
  });
export type PlayerHistoryCut = z.infer<typeof PlayerHistoryCut>;

export const PlayerOperationsQuery = z
  .strictObject({
    at: Timestamp.optional().meta({
      description:
        "Start the history at this instant instead of now. Keep sending it with `before` while paging back from it.",
    }),
    before: PlayerHistoryCut.optional().meta({
      description: "Read the page older than this cut: the `nextBefore` of the page shown.",
    }),
    newer: z.enum(["true", "false"]).optional().meta({
      description:
        "With `true`, read the page just newer than `before`, or than `at` with no `before`, and answer with the cut that reads it back.",
    }),
    limit: z.coerce.number().int().min(1).max(500).default(100).meta({
      description: "Maximum items on the page, 1 to 500. Defaults to 100.",
      example: 100,
    }),
  })
  .refine(query => query.newer !== "true" || query.before || query.at, {
    message: "Send `before` or `at` with `newer`.",
  })
  .meta({
    id: "PlayerOperationsQuery",
    description: "Where a page of a player's history starts.",
  });
export type PlayerOperationsQuery = z.infer<typeof PlayerOperationsQuery>;

export const OperationPosting = z
  .object({
    legIndex: z.number().int().nonnegative(),
    accountCode: z.string().meta({
      description:
        "The account's code: a system account's own code, or `player:` and the balance for a player's account.",
      example: "support_grants",
    }),
    accountUserId: UserId.nullable().meta({
      description: "The player whose balance this is, or null for a system account.",
    }),
    accountUser: LedgerPerson.nullable().meta({
      description: "The player whose balance this is, by name, or null for a system account.",
    }),
    side: z.enum(["debit", "credit"]),
    amountRaw: RawPaxTotal,
    bucket: PlayerBucket.nullable().meta({
      description: "The player balance posted to, or null for a system account.",
    }),
  })
  .meta({ id: "OperationPosting", description: "One line of an entry." });
export type OperationPosting = z.infer<typeof OperationPosting>;

export const OperationEntry = z
  .object({
    entryId: z.string().regex(POSITIVE_INTEGER),
    journalCode: z.string().meta({
      description: "Why the entry was made, as the ledger's journal code.",
      example: "support_grant",
    }),
    grantTier: z.string().nullable(),
    presetId: PresetId.nullable(),
    presetTitle: z.string().nullable().optional().meta({
      description: "The preset's title, from its active version or else its newest one.",
    }),
    recipient: LedgerPerson.nullable().meta({
      description: "Who a creator reward entry is for. Null for every other entry.",
    }),
    postings: z.array(OperationPosting),
  })
  .meta({ id: "OperationEntry", description: "One balanced entry of an operation." });
export type OperationEntry = z.infer<typeof OperationEntry>;

export const PlayerOperation = z
  .object({
    operationId: OperationId,
    recordedAt: Timestamp,
    effectKind: z.string().meta({ example: "support_grant" }),
    sourceSystem: z.string(),
    sourceRequestId: z.string(),
    playerChanges: BucketAmounts.meta({
      description:
        "What each of this player's balances gained or lost in the operation. Balances left out did not move.",
    }),
    entries: z.array(OperationEntry),
    supportAdjustment: z
      .object({ granter: LedgerPerson, reason: SupportAdjustmentReason, note: z.string() })
      .nullable()
      .meta({
        description: "Who made a support grant or removal, and why. Null for anything else.",
      }),
    heldFor: HeldFor.nullable().meta({
      description: "The hold a settlement charged. Null for anything else.",
    }),
  })
  .meta({ id: "PlayerOperation", description: "One operation that moved a player's credits." });
export type PlayerOperation = z.infer<typeof PlayerOperation>;

export const GameParams = z
  .strictObject({ gameId: GameId })
  .meta({ id: "GameParams", description: "A route parameter naming one game." });
export type GameParams = z.infer<typeof GameParams>;

export const ReleasedHold = z
  .object({
    holdId: HoldId,
    releasedAt: Timestamp,
    releaseReason: ReleaseReason,
    heldFor: HeldFor,
  })
  .meta({
    id: "ReleasedHold",
    description: "A hold the player shared that was released with nothing charged.",
  });
export type ReleasedHold = z.infer<typeof ReleasedHold>;

export const PlayerHistoryItem = z
  .discriminatedUnion("kind", [
    PlayerOperation.extend({ kind: z.literal("operation") }),
    ReleasedHold.extend({ kind: z.literal("releasedHold") }),
  ])
  .meta({
    id: "PlayerHistoryItem",
    description: "An operation that moved the player's credits, or a hold released uncharged.",
  });
export type PlayerHistoryItem = z.infer<typeof PlayerHistoryItem>;

export const PlayerOperationsResponse = z
  .object({
    items: z.array(PlayerHistoryItem).meta({
      description:
        "Up to `limit`, newest first by when each was recorded or released. An operation goes before a hold released at the same instant.",
    }),
    nextBefore: PlayerHistoryCut.nullable().meta({
      description: "The cut for the next older page, or null at the start of the history.",
    }),
    newer: PlayerHistoryCut.nullable().meta({
      description:
        "With `newer`: the cut that reads this same page as `before`. Null when the page reaches the newest item, and without `newer`.",
    }),
  })
  .meta({ id: "PlayerOperationsResponse", description: "One page of a player's history." });
export type PlayerOperationsResponse = z.infer<typeof PlayerOperationsResponse>;

export const HoldOutcome = z.enum(["settled", "released", "abandoned", "open"]).meta({
  id: "HoldOutcome",
  description:
    "How a hold ended. `settled`: charged its measured amount. `released`: ended with nobody charged. `abandoned`: passed its deadline before it settled. `open`: before its deadline, not yet settled or released.",
  example: "settled",
});
export type HoldOutcome = z.infer<typeof HoldOutcome>;

/** What each hold outcome means, in plain English. */
export const HOLD_OUTCOME_MEANINGS: Record<HoldOutcome, string> = {
  settled: "Charged its measured amount.",
  released: "Ended with nobody charged.",
  abandoned: "Passed its deadline before it settled.",
  open: "Before its deadline, not yet settled or released.",
};

export const GameHoldOutcomeFilter = z.enum(HoldOutcome.options).meta({
  id: "GameHoldOutcomeFilter",
  description: "Which of a game's holds to read: only those with this `HoldOutcome`.",
  example: "released",
});
export type GameHoldOutcomeFilter = z.infer<typeof GameHoldOutcomeFilter>;

export const GameHoldsQuery = z
  .strictObject({
    outcome: GameHoldOutcomeFilter.optional().meta({
      description: "Only holds that ended this way.",
    }),
    from: Timestamp.optional().meta({ description: "Only holds placed at or after this instant." }),
    to: Timestamp.optional().meta({ description: "Only holds placed before this instant." }),
    beforeHoldId: HoldId.optional().meta({
      description:
        "Read holds placed before this one: the `nextBeforeHoldId` of the previous page.",
    }),
    newer: z.enum(["true", "false"]).optional().meta({
      description:
        "With `true`, read the page just newer than the holds before `beforeHoldId`, starting with that hold, and answer with the cursor that reads it back.",
    }),
    limit: z.coerce.number().int().min(1).max(500).default(100).meta({
      description: "Maximum holds on the page, 1 to 500. Defaults to 100.",
      example: 100,
    }),
    payerUserId: UserId.optional().meta({
      description: "Only holds this player shared.",
    }),
  })
  .refine(query => query.newer !== "true" || query.beforeHoldId, {
    message: "Send `beforeHoldId` with `newer`.",
  })
  .meta({
    id: "GameHoldsQuery",
    description: "Which of a game's holds to read, and where the page starts.",
  });
export type GameHoldsQuery = z.infer<typeof GameHoldsQuery>;

export const GameHold = z
  .object({
    holdId: HoldId,
    holdRequestId: z.uuid().meta({
      description: "The holder's id for the hold. For a model call, its `call_id` in ClickHouse.",
    }),
    placedAt: Timestamp,
    outcome: HoldOutcome,
    estimateRaw: RawPaxTotal.meta({ description: "What the hold reserved when it was placed." }),
    measuredRaw: RawPaxTotal.nullable().meta({
      description: "What a settled hold charged in total. Null for every other outcome.",
    }),
    expiresAt: Timestamp.meta({
      description: "The hold's deadline. A hold not settled by then is abandoned.",
    }),
    releaseReason: ReleaseReason.nullable().meta({
      description: "Why a released or abandoned hold ended. Null for settled and open holds.",
    }),
    kind: HoldKind,
    presetId: PresetId.nullable().meta({
      description: "The game's preset. Null when the hold was placed without one.",
    }),
    presetTitle: z.string().nullable().meta({
      description: "The preset's title from its active or newest version, or null once it is gone.",
    }),
    payers: z.array(LedgerPerson).meta({ description: "Who shared the hold." }),
    agent: holdAgent,
    settlement: PlayerOperation.nullable().meta({
      description:
        "The operation that settled the hold, with its entries and postings. Null unless it settled.",
    }),
  })
  .meta({ id: "GameHold", description: "One hold placed for a game." });
export type GameHold = z.infer<typeof GameHold>;

export const GameHoldsResponse = z
  .object({
    holds: z.array(GameHold).meta({ description: "Newest placed first, up to `limit`." }),
    nextBeforeHoldId: HoldId.nullable().meta({
      description:
        "The cursor for the next older page. Null once nothing older matches, and with `newer`.",
    }),
    newer: HoldId.nullable().meta({
      description:
        "With `newer`: the `beforeHoldId` that reads this same page. Null when the page reaches the newest hold, and without `newer`.",
    }),
  })
  .meta({ id: "GameHoldsResponse", description: "One page of a game's holds." });
export type GameHoldsResponse = z.infer<typeof GameHoldsResponse>;

export const LedgerReportName = z
  .enum(["systemAccountFlows", "journalTotals"])
  .meta({ id: "LedgerReportName", description: "Which hourly rollup report to read." });
export type LedgerReportName = z.infer<typeof LedgerReportName>;

export const LedgerReportRange = z.enum(["24h", "7d", "30d", "month"]).meta({
  id: "LedgerReportRange",
  description:
    "A named window ending at the latest rolled-up hour. `24h` is read by the hour, `7d` by six UTC hours, and `30d` and `month` by the UTC day.",
  example: "24h",
});
export type LedgerReportRange = z.infer<typeof LedgerReportRange>;

export const LedgerReportParams = z
  .strictObject({ report: LedgerReportName })
  .meta({ id: "LedgerReportParams", description: "The report to read." });
export type LedgerReportParams = z.infer<typeof LedgerReportParams>;

export const LedgerReportQuery = z
  .strictObject({
    range: LedgerReportRange.default("24h"),
    back: periodsBack(
      "How many ranges back to read: 0 for the newest, which ends at the latest rolled-up hour; 1 for the one just before it; and so on. `month` steps by calendar month.",
    ),
  })
  .meta({ id: "LedgerReportQuery", description: "The window to read." });
export type LedgerReportQuery = z.infer<typeof LedgerReportQuery>;

export const SystemAccountKind = z.enum(["granted", "used", "expired", "removed", "pending"]);
export type SystemAccountKind = z.infer<typeof SystemAccountKind>;

export const SystemAccountFlow = z
  .object({
    accountCode: z.string().meta({ example: "support_grants" }),
    accountKind: SystemAccountKind,
    debitTotalRaw: RawPaxSum,
    creditTotalRaw: RawPaxSum,
    flowTotalRaw: SignedRawPaxSum.meta({
      description:
        "The account's flow in its natural direction: debits less credits for granted accounts, credits less debits for the rest.",
    }),
  })
  .meta({ id: "SystemAccountFlow", description: "What moved through one system account." });
export type SystemAccountFlow = z.infer<typeof SystemAccountFlow>;

export const JournalTotal = z
  .object({
    journalCode: z.string().meta({ example: "ai_settlement" }),
    movedTotalRaw: RawPaxSum,
    entryCount: z.number().int().nonnegative(),
  })
  .meta({ id: "JournalTotal", description: "What one journal moved, and in how many entries." });
export type JournalTotal = z.infer<typeof JournalTotal>;

const reportWindow = {
  from: Timestamp.nullable().meta({
    description: "The window's start. Null until data is rolled up.",
  }),
  to: Timestamp.nullable().meta({
    description:
      "The window's exclusive end: the latest rolled-up hour. Null until data is rolled up.",
  }),
  step: z.enum(["hour", "sixHours", "day"]).meta({
    description: "How long each bucket is. Six-hour buckets start at 00, 06, 12, and 18 UTC.",
  }),
};

function reportBuckets<Row extends z.ZodType>(row: Row) {
  return z.array(z.object({ bucketStart: Timestamp, rows: z.array(row) })).meta({
    description:
      "Every bucket in the window, oldest first. Each holds a row only for the keys with activity in it.",
  });
}

export const LedgerReportResponse = z
  .discriminatedUnion("report", [
    z.object({
      report: z.literal("systemAccountFlows"),
      ...reportWindow,
      buckets: reportBuckets(SystemAccountFlow),
      totals: z.array(SystemAccountFlow),
    }),
    z.object({
      report: z.literal("journalTotals"),
      ...reportWindow,
      buckets: reportBuckets(JournalTotal),
      totals: z.array(JournalTotal),
    }),
  ])
  .meta({
    id: "LedgerReportResponse",
    description: "One report over a named window, per bucket and in total.",
  });
export type LedgerReportResponse = z.infer<typeof LedgerReportResponse>;
